import { SkyChartBlock, SkyCopyButton, SkyText } from "@skypointcloud/skypoint-ui";
import type { RowsTrendPoint } from "@shared/types/reliability";
import type { EntityProgressRow } from "@shared/types/runs";
import { ReliabilityHelpLink } from "@/components/reliability/Freshness";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { useRowsTrend } from "@/hooks/use-reliability";
import { chartTimeLabels, formatInstant, zoneCaption } from "@/lib/time/displayTime";

/**
 * Rows per load on the run's page (#828, `docs/requirements/slo-canary.md` R6.2 and R8.2): a bar
 * for each of the connection's recent loads, oldest on the left, this run's bar named "This run",
 * from skyData's own load records (`GET /dataflows/:id/rows-trend`). "Copy counts" copies this
 * run's tables as the `VALUES` lines `scripts/snowflake/verify-landed-counts.sql` takes, which the
 * customer runs in their own warehouse; skyData reads nothing back (ADR 0011).
 */

export const THIS_RUN = "This run";

/** A SQL string literal: the name in single quotes, a quote inside doubled. */
function sqlText(value: string): string {
  return `'${value.replaceAll("'", "''")}'`;
}

function sqlNumber(value: number | null | undefined): string {
  return value === null || value === undefined ? "NULL" : String(value);
}

/**
 * This run's landed tables of one connection, one `('<table>', <rows loaded>, <rows in the table
 * after the load>)` line each, joined by a comma and a new line, sorted by table name. Only a table
 * whose load succeeded and named its target counts.
 */
export function copyCountsText(entities: readonly EntityProgressRow[], dataflowId: string): string {
  return entities
    .filter(
      (row) =>
        row.dataflowId === dataflowId &&
        row.load?.status === "succeeded" &&
        typeof row.load.targetTable === "string" &&
        row.load.targetTable !== "",
    )
    .map((row) => row.load as NonNullable<EntityProgressRow["load"]>)
    .sort((a, b) => ((a.targetTable as string) < (b.targetTable as string) ? -1 : 1))
    .map(
      (load) =>
        `(${sqlText(load.targetTable as string)}, ${sqlNumber(load.rowsLoaded)}, ${sqlNumber(load.totalRecords)})`,
    )
    .join(",\n");
}

/**
 * The chart's bars, oldest first, in one series (a second series for this run printed "–" in the
 * hover and the Table and drew every bar at half width, #1215). The axis names each load by its day
 * in the viewer's zone (`chartTimeLabels`: short enough that the labels `SkyChartBlock` draws never
 * touch), this run's own bar by "This run". The exact time of each load is in `loadTimes`, the list
 * under the chart, because the axis, hover and Table carry the short label only.
 */
export function trendBars(
  points: readonly RowsTrendPoint[],
  runId: string,
  zone: string,
): { categories: string[]; values: number[] } {
  const ordered = [...points].reverse();
  const times = chartTimeLabels(
    ordered.map((point) => point.finishedAt),
    zone,
  );
  return {
    categories: ordered.map((point, i) => (point.runId === runId ? THIS_RUN : times[i]!)),
    values: ordered.map((point) => point.totalRowsLoaded ?? 0),
  };
}

/** One line for each load, oldest first: its full time in `zone`, its rows, and "This run". */
export function loadTimes(
  points: readonly RowsTrendPoint[],
  runId: string,
  zone: string,
): string[] {
  return [...points].reverse().map((point) => {
    const rows = (point.totalRowsLoaded ?? 0).toLocaleString("en-US");
    const mine = point.runId === runId ? " (this run)" : "";
    return `${formatInstant(point.finishedAt, zone, "short")}: ${rows} ${rows === "1" ? "row" : "rows"}${mine}`;
  });
}

/** This run's own records show a load of the connection (a table that loaded or tried to). */
export function ranLoad(entities: readonly EntityProgressRow[], dataflowId: string): boolean {
  return entities.some(
    (row) =>
      row.dataflowId === dataflowId &&
      row.load !== undefined &&
      row.load !== null &&
      row.load.status !== "skipped",
  );
}

/**
 * What the chart says about this run. The trend holds only the connection's newest loads, so a run
 * left out of it is not a run that loaded nothing: its own records decide (#1043).
 */
export function thisRunSentence(
  inTrend: boolean,
  ownLoad: boolean,
  runTerminal: boolean,
  name: string,
): string {
  if (inTrend) return `"${THIS_RUN}" is this run.`;
  if (!runTerminal) return "This run has not loaded yet.";
  return ownLoad
    ? "This run is older than the loads shown."
    : `This run loaded no rows for ${name}.`;
}

function ConnectionRowsTrend({
  runId,
  dataflowId,
  name,
  entities,
  runTerminal,
}: {
  runId: string;
  dataflowId: string;
  name: string;
  entities: readonly EntityProgressRow[];
  runTerminal: boolean;
}) {
  const zone = useDisplayTz();
  // #1043: a finished run's page reads the trend fresh on every mount, and the page invalidates it
  // once when the run finishes (`useLiveRun`). Nothing polls while the run is active.
  const trend = useRowsTrend(dataflowId, 30, runTerminal);
  const counts = copyCountsText(entities, dataflowId);
  const points = trend.data?.points ?? [];
  const bars = trendBars(points, runId, zone);
  const ownLoad = ranLoad(entities, dataflowId);

  return (
    <div className="space-y-2" data-testid={`rows-trend-${dataflowId}`}>
      {trend.isError ? (
        <SkyText size={200} color="muted" block testId={`rows-trend-error-${dataflowId}`}>
          Rows per load could not be read.
        </SkyText>
      ) : points.length === 0 ? (
        trend.isPending ? null : (
          <SkyText size={200} color="muted" block testId={`rows-trend-empty-${dataflowId}`}>
            {runTerminal ? `${name} has no finished load.` : `${name} has no finished load yet.`}
          </SkyText>
        )
      ) : (
        <SkyChartBlock
          title={`Rows per load: ${name}`}
          description={`The last ${points.length} ${points.length === 1 ? "load" : "loads"}, oldest first. ${zoneCaption(zone)}. ${thisRunSentence(
            points.some((p) => p.runId === runId),
            ownLoad,
            runTerminal,
            name,
          )}`}
          type="bar"
          categories={bars.categories}
          series={[{ name: "Rows loaded", values: bars.values }]}
          height={160}
          testId={`rows-trend-chart-${dataflowId}`}
        />
      )}
      {points.length > 0 ? (
        <details data-testid={`rows-trend-times-${dataflowId}`}>
          <summary className="cursor-pointer text-sm">When each load finished</summary>
          <ul className="mt-1 space-y-0.5 text-sm">
            {loadTimes(points, runId, zone).map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </details>
      ) : null}
      {counts ? (
        <div className="flex flex-wrap items-center gap-2">
          <SkyCopyButton
            value={counts}
            label="Copy counts"
            copiedLabel="Counts copied"
            showLabel
            size="small"
            testId={`rows-trend-copy-${dataflowId}`}
          />
          <SkyText size={200} color="muted">
            For the check script you run in Snowflake.
          </SkyText>
        </div>
      ) : null}
    </div>
  );
}

export function RowsTrendCard({
  runId,
  dataflowIds,
  connectionNames,
  entities,
  runTerminal,
}: {
  runId: string;
  dataflowIds: readonly string[];
  connectionNames: ReadonlyMap<string, string>;
  entities: readonly EntityProgressRow[];
  /** The run has reached a finished status (succeeded, partly succeeded, failed, cancelled). */
  runTerminal: boolean;
}) {
  if (dataflowIds.length === 0) return null;
  return (
    <section aria-label="Rows per load" className="space-y-3" data-testid="rows-trend">
      {dataflowIds.map((id) => (
        <ConnectionRowsTrend
          key={id}
          runId={runId}
          dataflowId={id}
          name={connectionNames.get(id) ?? "This connection"}
          entities={entities}
          runTerminal={runTerminal}
        />
      ))}
      <ReliabilityHelpLink screen="runDetail" testId="rows-trend-help" />
    </section>
  );
}
